import moment from "moment-timezone";
import { meetEligibilityMap } from "src/utils/meetConstants";
import { MeetCourseType, MeetEventType } from "types/code-generator";

export function getBestMeetEventTime(args: {
  meet: {
    course?: MeetCourseType;
    eligibleCourses?: string;
    enforceTimeStandards: boolean;
  };
  meetEvent: {
    id: string;
    fasterThanYards?: Date;
    fasterThanSCM?: Date;
    fasterThanLCM?: Date;
    stroke?: string;
    distance?: number;
    type?: MeetEventType;
    slowerThanYards?: Date;
    slowerThanSCM?: Date;
    slowerThanLCM?: Date;
  };
  meetResults: {
    stroke: string;
    distance: number;
    type: MeetEventType;
    course: MeetCourseType;
    time: Date;
    finals?: boolean;
    meetTitle?: string;
    meetStartDate?: Date;
    meet?: {
      title?: string;
      startDate?: Date;
    };
  }[];
  courses?: MeetCourseType[];
}) {
  if (!args.meetEvent) {
    return null;
  }

  let courses = [args.meet.course];

  if (
    args.meet.eligibleCourses &&
    meetEligibilityMap[args.meet.eligibleCourses]
  ) {
    courses = meetEligibilityMap[args.meet.eligibleCourses];
  }

  if (args.courses) {
    courses = args.courses;
  }

  let bestTime: (typeof args.meetResults)[0] = null;
  const hasFasterThan =
    args.meetEvent.fasterThanLCM ||
    args.meetEvent.fasterThanSCM ||
    args.meetEvent.fasterThanYards;
  const hasSlowerThan =
    args.meetEvent.slowerThanLCM ||
    args.meetEvent.slowerThanSCM ||
    args.meetEvent.slowerThanYards;
  const isRelay = args.meetEvent.type === MeetEventType.Relay;

  for (const c of courses) {
    let fasterTimeStandardTime =
      args.meetEvent.fasterThanYards && moment(args.meetEvent.fasterThanYards);
    let slowerThanTimeStandardTime =
      args.meetEvent.slowerThanYards && moment(args.meetEvent.slowerThanYards);

    if (c === MeetCourseType.SCM) {
      fasterTimeStandardTime =
        args.meetEvent.fasterThanSCM && moment(args.meetEvent.fasterThanSCM);
      slowerThanTimeStandardTime =
        args.meetEvent.slowerThanSCM && moment(args.meetEvent.slowerThanSCM);
    } else if (c === MeetCourseType.LCM) {
      fasterTimeStandardTime =
        args.meetEvent.fasterThanLCM && moment(args.meetEvent.fasterThanLCM);
      slowerThanTimeStandardTime =
        args.meetEvent.slowerThanLCM && moment(args.meetEvent.slowerThanLCM);
    }

    const bestCourseTime = args.meetResults.find((r) => {
      const strokeMatch = r.stroke === args.meetEvent.stroke;
      let distanceMatch = r.distance === args.meetEvent.distance;

      if (isRelay) {
        distanceMatch = r.distance === args.meetEvent.distance / 4;
      }

      const typeMatch = isRelay ? true : r.type === args.meetEvent.type;
      const courseMatch = r.course === c;

      return strokeMatch && distanceMatch && typeMatch && courseMatch;
    });

    if (!bestCourseTime) {
      continue;
    }

    const userTime = moment(bestCourseTime.time);
    let fasterThanTimeEligible = false;
    let slowerThanTimeEligible = false;

    userTime.set({
      year: 2025,
      month: 0,
      date: 1,
      hour: 0,
    });

    fasterTimeStandardTime &&
      fasterTimeStandardTime.set({
        year: 2025,
        month: 0,
        date: 1,
        hour: 0,
      });

    slowerThanTimeStandardTime &&
      slowerThanTimeStandardTime.set({
        year: 2025,
        month: 0,
        date: 1,
        hour: 0,
      });

    if (fasterTimeStandardTime && slowerThanTimeStandardTime) {
      const timeValid =
        userTime.isSameOrBefore(fasterTimeStandardTime) &&
        userTime.isAfter(slowerThanTimeStandardTime);

      if (timeValid) {
        fasterThanTimeEligible = true;
        slowerThanTimeEligible = true;
      }
    } else if (slowerThanTimeStandardTime) {
      const timeValid = userTime.isAfter(slowerThanTimeStandardTime);

      if (timeValid) {
        slowerThanTimeEligible = true;
      }
    } else if (fasterTimeStandardTime) {
      const timeValid = userTime.isSameOrBefore(fasterTimeStandardTime);

      if (timeValid) {
        fasterThanTimeEligible = true;
      }
    }

    let timeEligible = fasterThanTimeEligible || slowerThanTimeEligible;

    if (
      args.meet.enforceTimeStandards === false ||
      (!hasFasterThan && !hasSlowerThan)
    ) {
      timeEligible = true;
    }

    // if (args.meetEvent.id === "cm5x3wl3s0a2xol1j4rxmkilp") {
    //   console.log("fasterThanTimeEligible", fasterThanTimeEligible);
    //   console.log("slowerThanTimeEligible", slowerThanTimeEligible);
    //   console.log("timeEligible", timeEligible);
    //   console.log("bestCourseTime", bestCourseTime);
    // }

    if (
      hasSlowerThan &&
      slowerThanTimeStandardTime &&
      !hasFasterThan &&
      userTime.isBefore(slowerThanTimeStandardTime) &&
      args.meet.enforceTimeStandards
    ) {
      // Break early because the user is faster than the slower than time standard
      break;
    }

    if (timeEligible || !args.meet.enforceTimeStandards) {
      bestTime = bestCourseTime;
      break;
    }
  }

  return bestTime;
}
